
import BillOfMaterials from "@/components/BillOfMaterials.vue";
import { appState, importSalesOrder } from "@/main";
import { Builder, saveAddresses } from "@/ts/api";
import { Address, currency, getItems, Item } from "@/ts/api/store";
import { defineComponent } from "@vue/runtime-core";
import { f7, f7ready } from "framework7-vue";
import { Router } from "framework7/types";
import { nanoid } from "nanoid";
import { PropType, ref } from "vue";
// import CardPointe, { TokenizerResponse } from "./components/CardPointe.vue";
// Country/Region data
import countries from "i18n-iso-countries";
// eslint-disable-next-line @typescript-eslint/no-var-requires
const enLocale = require("i18n-iso-countries/langs/en.json");
import iso3166 from "iso-3166-2";

interface Package {
  name: string;
  willCall: boolean;
  address: Address;
  shipDate: Date[];
  BOM: Item[];
}

export enum SalesOrderStatus {
  Estimate = 10,
  Issued = 20,
  Cancelled = 85,
  Historical = 95,
}
export enum SalesOrderItemType {
  Sale = 10,
  MiscSale = 11,
  DropShip = 12,
  CreditReturn = 20,
  MiscCredit = 21,
  DiscountPercentage = 30,
  DiscountAmount = 31,
  Subtotal = 40,
  AssocPrice = 50,
  Shipping = 60,
  Tax = 70,
  Kit = 80,
  None = 90,
  BOMConfigurationItem = 100,
}
export interface SalesOrder {
  builderID?: string;
  number: string;
  status: SalesOrderStatus;
  billingAddress: Address;
  shippingAddress: Address;
  carrierName: string;
  taxRateName: string;
  orderDateScheduled: string;
  isCcPaymentBranch: true;
  billOfMaterials: [
    {
      type: SalesOrderItemType;
      quantity: number;
      productDescription: string;
      productNumber: string;
      uom: "ea";
      loyaltyPoints: number;
      notes: string;
    }
  ];
  totalPrice?: number;
  PONum?: string;
  cardPointeData?: {
    /** Cardpointe account token (e.g. 9440670166031111) */
    account: string;
    /** MMDD (e.g. 0826) */
    expiry: string;
    /** USD (e.g. 12.38) */
    amount: number;
    /** Save this account on CardPointe for future use */
    saveAccount?: false;
    /** Postal code (e.g. 12345) */
    postal: string;
  };
}

// Removed defaultAddress in favor of cloning saved addresses with sensible defaults

export default defineComponent({
  props: {
    builder: Object as PropType<Builder>,
    PONum: String,
    items: Object as PropType<Item[]>,
    usePackages: Object as PropType<boolean>,
    callback: Object as PropType<() => Promise<void>>,
    f7route: Object as PropType<Router.Route>,
    f7router: Object as PropType<Router.Router>,
  },
  components: {
    BillOfMaterials,
    // CardPointe,
  },
  setup(props) {
    const packages = ref([] as Package[]);

    let items = props.items ?? [];

    // Register country locale once
    try { countries.registerLocale(enLocale as any); }
    catch (_err) { console.debug("i18n-iso-countries locale already registered"); }
    const countryOptions = Object.entries(countries.getNames("en", { select: "official" }) as Record<string, string>)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const getRegions = (countryCode?: string): { code: string; name: string }[] => {
      if (!countryCode) return [];
      try {
        const map = (iso3166 as any).subdivision(countryCode) as Record<string, { name: string }> | undefined;
        if (!map) return [];
        return Object.keys(map).map((k) => {
          const entry = map[k];
          const short = k.includes("-") ? k.split("-")[1] : k;
          return { code: short, name: entry.name };
        }).sort((a, b) => a.name.localeCompare(b.name));
      } catch {
        return [];
      }
    };

    const normalizeCountryCode = (value?: string): string => {
      if (!value || value.trim().length === 0) return "US";
      const v = value.trim();
      if (/^[A-Za-z]{2}$/.test(v)) return v.toUpperCase();
      const code = (countries as any).getAlpha2Code(v, "en");
      return (code || "US").toUpperCase();
    };

    const cloneAddress = (a?: Address): Address => ({
      name: a?.name ?? "",
      street: a?.street ?? "",
      city: a?.city ?? "",
      state: a?.state ?? "",
      zip: a?.zip ?? "",
      country: normalizeCountryCode(a?.country ?? "US"),
      phone: a?.phone ?? "",
    });

    // Calculate the start ship date. Don't allow saturdays or sundays
    var defaultShipDate = new Date(Date.now() + 172800000);
    if (defaultShipDate.getDay() == 0) {
      // Add a day
      defaultShipDate = new Date(defaultShipDate.getTime() + 86400000);
    } else if (defaultShipDate.getDay() == 6) {
      // Add 2 days
      defaultShipDate = new Date(defaultShipDate.getTime() + (86400000 * 2));
    }

    if (props.usePackages) {
      // separate the items into groups via their packageID
      let itemMap = new Map<string, Item[]>();
      for (let i = 0; i < items.length; i++) {
        const _item = items[i];
        if (!itemMap.has(_item.packageID)) {
          itemMap.set(_item.packageID, []);
        }
        itemMap.get(_item.packageID)?.push(_item);
      }

      // sort by keys
      itemMap = new Map([...itemMap].sort());



      // generate the package array from the itemMap
      let idx = 0;
      itemMap.forEach((packageItems) => {
        packages.value.push({
          name: packageItems[0].packageName,
          willCall: false,
          address: cloneAddress(
            appState.builderData?.savedAddresses["package-" + idx] ??
            undefined
          ),
          shipDate: [defaultShipDate],
          BOM: packageItems,
        });
        idx++;
      });
    } else {
      packages.value.push({
        name: "Order",
        willCall: false,
        address: cloneAddress(
          appState.builderData?.savedAddresses["package-0"] ??
          undefined
        ),
        shipDate: [defaultShipDate],
        BOM: items,
      });
    }

    let totalPrice = ref(0);
    f7ready(() => {
      if (appState.admin) {
        //get builder pricing
        let _preloader = f7.dialog.preloader("Loading builder pricing...");
        getItems(items.map(i => i.sku), props.builder?.customerID).then((newValues) => {

          newValues.forEach((newItem) => {
            const oldItem = items.find((i) => i.sku === newItem.sku);
            if (oldItem) {
              oldItem.updateItem(newItem);
            }
          });
          packages.value.forEach((p) =>
            p.BOM.forEach((i) => (totalPrice.value += i.price * i.count)));
        }).finally(() => _preloader.close());
      } else {
        packages.value.forEach((p) =>
          p.BOM.forEach((i) => (totalPrice.value += i.price * i.count))
        );
      }
    });


    return {
      packages: packages,
      totalPrice: totalPrice,
      billToAddress: ref(
        cloneAddress(appState.builderData?.savedAddresses["bill-to"])
      ),
      countryOptions,
      getRegions,
      placingOrder: ref(false),
      paymentMethod: ref("invoice"),
      // CCTokenData: ref() as Ref<TokenizerResponse | undefined>,
    };
  },
  methods: {
    postalCodeLabel(country?: string): string {
      const c = (country || 'US').toUpperCase();
      return c === 'US' ? 'Zip' : 'Postal Code';
    },
    postalCodePattern(country?: string): string {
      const c = (country || 'US').toUpperCase();
      switch (c) {
        case 'US':
          return '^\\d{5}(?:-\\d{4})?$'; // 12345 or 12345-6789
        case 'CA':
          return '^[ABCEGHJ-NPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z] ?\\d[ABCEGHJ-NPRSTV-Z]\\d$'; // A1A 1A1
        case 'GB':
          return '^(GIR 0AA|(?:(?:[A-Z]{1,2}[0-9][0-9A-Z]?|[A-Z][A-Z][0-9][0-9A-Z]?) ?[0-9][A-Z]{2}))$';
        case 'DE':
          return '^\\d{5}$';
        case 'FR':
          return '^\\d{5}$';
        case 'AU':
          return '^\\d{4}$';
        case 'IT':
          return '^\\d{5}$';
        case 'ES':
          return '^\\d{5}$';
        case 'NL':
          return '^[1-9]\\d{3} ?[A-Z]{2}$';
        case 'SE':
          return '^(SE-)?\\d{3} ?\\d{2}$';
        case 'CH':
          return '^\\d{4}$';
        case 'AT':
          return '^\\d{4}$';
        case 'BE':
          return '^\\d{4}$';
        case 'DK':
          return '^\\d{4}$';
        case 'NO':
          return '^\\d{4}$';
        case 'BR':
          return '^\\d{5}-?\\d{3}$';
        case 'MX':
          return '^\\d{5}$';
        case 'NZ':
          return '^\\d{4}$';
        case 'IE':
          return '^[A-Z0-9]{3} ?[A-Z0-9]{4}$'; // Eircode simplified
        default:
          return '^.{3,10}$'; // generic: 3-10 chars
      }
    },
    postalCodeError(country?: string): string {
      const c = (country || 'US').toUpperCase();
      const map: Record<string, string> = {
        US: 'Please enter a valid ZIP code (e.g., 12345 or 12345-6789)',
        CA: 'Please enter a valid Canadian postal code (e.g., A1A 1A1)',
        GB: 'Please enter a valid UK postcode (e.g., SW1A 1AA)',
        DE: 'Bitte geben Sie eine gültige Postleitzahl ein (5 Ziffern)',
        FR: 'Veuillez saisir un code postal valide (5 chiffres)',
        AU: 'Please enter a valid Australian postcode (4 digits)',
        IT: 'Inserisci un CAP valido (5 cifre)',
        ES: 'Introduzca un código postal válido (5 dígitos)',
        NL: 'Voer een geldige postcode in (e.g., 1234 AB)',
        SE: 'Ange ett giltigt postnummer (e.g., 123 45)',
        CH: 'Bitte geben Sie eine gültige Postleitzahl ein (4 Ziffern)',
        AT: 'Bitte geben Sie eine gültige Postleitzahl ein (4 Ziffern)',
        BE: 'Veuillez saisir un code postal valide (4 chiffres)',
        DK: 'Indtast et gyldigt postnummer (4 cifre)',
        NO: 'Vennligst skriv inn et gyldig postnummer (4 sifre)',
        BR: 'Insira um CEP válido (e.g., 12345-678)',
        MX: 'Ingrese un código postal válido (5 dígitos)',
        NZ: 'Please enter a valid New Zealand postcode (4 digits)',
        IE: 'Please enter a valid Eircode (e.g., A65 F4E2)',
      };
      return map[c] || 'Please enter a valid postal code';
    },
    validateAddress(a: Address, phoneRequired = false): boolean {
      const requiredFields = ['name', 'street', 'city', 'state', 'zip', 'country'];
      if (phoneRequired) {
        requiredFields.push('phone');
      }

      for (const field of requiredFields) {
        const value = a[field as keyof Address];
        if (!value || typeof value !== 'string' || value.trim().length === 0) {
          return false;
        }
      }

      // Basic format validation
      const stateRegexUS = /^[A-Z]{2}$/;
      const countryAllowed = (a.country || '').trim().length > 0;

      if (phoneRequired) {
        const phoneRegex = /[0-9-() ]{10,}/;
        if (!phoneRegex.test(a.phone || '')) return false;
      }

      const zipPattern = new RegExp(this.postalCodePattern(a.country));
      const zipOk = zipPattern.test((a.zip || '').toUpperCase());
      const isUS = (a.country || '').toUpperCase() === 'US';
      const stateOk = isUS ? stateRegexUS.test(a.state || '') : (a.state || '').trim().length >= 2;

      return (
        zipOk &&
        stateOk &&
        countryAllowed &&
        (a.street || '').trim().length >= 5 &&
        (a.city || '').trim().length >= 2
      );
    },
    // Returns true if the given date is invalid for shipping
    notValidShipDate(date: Date): boolean {
      if (date.getDay() == 0 || date.getDay() == 6) {
        return true;
      } else {
        return false;
      }
    },
    async purchase() {
      if (!this.validateAddress(this.billToAddress, true)) {
        f7.dialog.alert("You're missing billing address info.");
      }

      this.placingOrder = true;
      try {
        // Get builder number
        let builderNumber = "unknown";
        if (this.builder?.customer.number)
          builderNumber = this.builder.customer.number;
        else throw "number not found";

        // start compiling individual sales orders from packages
        let a = {} as { [key: string]: Address };
        let orders: SalesOrder[] = [];
        this.packages.forEach((p, i) => {
          // validate address & shipping date
          if (p.shipDate)
            if (!p.willCall && !this.validateAddress(p.address)) {
              f7.dialog.alert(
                `You're missing shipping info for the ${p.name} Package`
              );
              throw "missing details";
            }

          // use provided country values
          let ba: Address = this.billToAddress as Address;
          let sa: Address = p.address as Address;

          let order = {
            number: builderNumber + "-" + nanoid(8),
            // specify custom builder ID if user is an admin
            builderID: this.builder?.builderID,
            // usually a job ID
            PONum: this.PONum,
            status: SalesOrderStatus.Issued,
            billingAddress: ba,
            shippingAddress: !p.willCall ? sa : {},
            carrierName: p.willCall ? "Will Call" : "UPS",
            taxRateName: "None",
            orderDateScheduled: `${p.shipDate[0].getMonth() + 1
              }/${p.shipDate[0].getDate()}/${p.shipDate[0].getFullYear()}`,
            billOfMaterials: p.BOM.map((item) => {
              return {
                type: SalesOrderItemType.Sale,
                quantity: item.count,
                productNumber: item.number,
                productDescription: item.name,
                uom: "ea",
                notes: item.notes,
                loyaltyPoints: item.loyaltyPoints,
              };
            }),
            totalPrice: this.totalPrice,
          } as SalesOrder;

          orders.push(order);

          // save package addresses (0-based index to match load)
          a[`package-${i}`] = p.address;
        });
        a["bill-to"] = this.billToAddress;
        saveAddresses(a);
        for await (const order of orders) {
          await importSalesOrder(order).catch((e) => {
            console.error("SO FAILED", e);
            f7.dialog.alert(
              e.message ?? "Failed to submit order. Please contact support.",
              "An error occurred"
            );
            throw e;
          });
        }
        if (this.callback) await this.callback();
        this.f7router?.navigate("/jobs");
        f7.dialog.alert(
          "Our customer service team will be reaching out to complete your payment. Please have your payment information ready. For questions, please call (602) 278-3531 or email info@pulproducts.com",
          "Purchase Request Submitted"
        );
      } catch (e) {
        console.error("failed to place order:", e);
      } finally {
        this.placingOrder = false;
      }
    },
    // updateToken(t: TokenizerResponse) {
    //   this.CCTokenData = t;
    // }
  },
  computed: {
    readablePrice(): string {
      return currency.format(this.totalPrice);
    },
    shippingCostText(): string {
      const anyNonUS = (this.packages ?? []).some(
        (p: any) => !p.willCall && (p.address?.country ?? 'US') !== 'US'
      );

      if (anyNonUS) {
        return "Shipping and import fees will be included in the quote.";
      }
      if (this.totalPrice >= 5000) return "Free shipping.";
      return "Shipping costs will be included in the quote.";
    },
    validatePurchase(): boolean {
      return true;
    },
    appState: () => appState,
  },
});
